import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

/**
 * Configuration i18next optimisée avec lazy loading
 * 
 * ============================================================
 * 📚 LEÇON : Lazy Loading des traductions
 * ============================================================
 * 
 * AVANT (config.js) :
 *   → 20 fichiers JSON importés statiquement
 *   → ~150 Ko chargés au démarrage
 *   → Parsing JS bloquant : ~180 ms de TBT
 * 
 * APRÈS (config-lazy.js) :
 *   → Seul 'common' chargé au démarrage
 *   → Les autres namespaces chargés à la demande
 *   → Parsing JS réduit : ~30 ms de TBT
 *   → Gain : -150 ms de TBT
 * 
 * COMMENT ÇA MARCHE ?
 *   1. HttpBackend charge les fichiers depuis /public/locales/
 *   2. Les traductions sont mises en cache après le premier chargement
 *   3. Chaque page spécifie ses namespaces : useTranslation(['common', 'home'])
 *   4. Si le namespace est déjà en cache, pas de requête réseau
 * 
 * ============================================================
 */

i18n
  .use(HttpBackend) // ✅ Charge les traductions à la demande via HTTP
  .use(LanguageDetector) // Détecte la langue du navigateur
  .use(initReactI18next) // Intégration React
  .init({
    fallbackLng: 'fr',     // Langue par défaut
    defaultNS: 'common',    // Namespace par défaut
    lng: 'fr',              // Force le français au démarrage
    
    // ─────────────────────────────────────────────────────────────
    // Configuration du backend HTTP
    // ─────────────────────────────────────────────────────────────
    backend: {
      // Chemin vers les fichiers de traduction
      // {{lng}} = langue (fr, en)
      // {{ns}} = namespace (common, home, about, etc.)
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      
      // Ne pas charger plusieurs namespaces en une seule requête
      // (permet un meilleur contrôle du cache)
      allowMultiLoading: false,
      
      // Options de requête
      requestOptions: {
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'default',
      },
    },
    
    // ─────────────────────────────────────────────────────────────
    // Namespaces chargés au démarrage
    // ─────────────────────────────────────────────────────────────
    // ✅ Seul 'common' est chargé immédiatement
    // Les autres seront chargés à la demande par les pages
    ns: ['common'],
    
    // ─────────────────────────────────────────────────────────────
    // Détection de la langue
    // ─────────────────────────────────────────────────────────────
    detection: {
      order: ['localStorage', 'navigator'], // Ordre de détection
      caches: ['localStorage'],              // Sauvegarde dans localStorage
      lookupLocalStorage: 'i18nextLng',      // Clé localStorage
    },
    
    // ─────────────────────────────────────────────────────────────
    // Options React
    // ─────────────────────────────────────────────────────────────
    interpolation: {
      escapeValue: false,   // React échappe déjà les valeurs
    },
    
    react: {
      useSuspense: true,    // ✅ Réactivé avec le lazy loading
      // Suspense affiche un fallback pendant le chargement des traductions
    },
    
    // ─────────────────────────────────────────────────────────────
    // Options de chargement
    // ─────────────────────────────────────────────────────────────
    load: 'languageOnly',   // Charge uniquement 'fr' et pas 'fr-FR'
    
    // Précharge les namespaces critiques (optionnel)
    // preload: ['common', 'home'],
  });

export default i18n;
